import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { notificar } from '../utils/notify';
import { formatarCNPJ } from '../utils/formatters';
import '../styles/Pgfn.css';

const Pgfn = () => {
  const [activeTab, setActiveTab] = useState('consulta');
  const [empresas, setEmpresas] = useState([]);
  const [selecionadas, setSelecionadas] = useState([]);
  const [mensagem, setMensagem] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [gerando, setGerando] = useState(false);
  const [gerandoSegundaVia, setGerandoSegundaVia] = useState(false);
  const [emitindoSegundaViaDireta, setEmitindoSegundaViaDireta] = useState(false);
  
  // Estados para segunda via
  const [mostrarModalSegundaVia, setMostrarModalSegundaVia] = useState(false);
  const [datasSegundaVia, setDatasSegundaVia] = useState({
    data_inicio: '2025-01-01',
    data_fim: new Date().toISOString().split('T')[0] // Data atual
  });
  
  // Estados para a tab de certidões armazenadas
  const [certidoes, setCertidoes] = useState([]);
  const [certidoesFiltradas, setCertidoesFiltradas] = useState([]);
  const [carregandoCertidoes, setCarregandoCertidoes] = useState(false);
  const [filtroCertidao, setFiltroCertidao] = useState('');
  
  // Filtros para as certidões armazenadas
  const [filtrosCertidoes, setFiltrosCertidoes] = useState({
    cnpj: '',
    razao_social: '',
    tipo_certidao: '',
    data_emissao: '',
    data_validade: '',
    codigo: ''
  });

  const [filtros, setFiltros] = useState({
    razaosocial: '',
    cnpj: ''
  });

  useEffect(() => { notificar(mensagem); }, [mensagem]);

  // Função para carregar empresas
  const carregarEmpresas = useCallback(() => {
    setCarregando(true);
    setMensagem('');

    const accessToken = localStorage.getItem('access');
    
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/empresas-das/`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      })
      .then((res) => {
        if (Array.isArray(res.data)) {
          setEmpresas(res.data);
        } else {
          console.error('Dados recebidos não são um array:', res.data);
          setMensagem('Formato de dados inválido');
        }
        setCarregando(false);
      })
      .catch((error) => {
        console.error('Erro ao carregar empresas:', error);
        const errorMsg = error.response?.data?.erro || 
                         error.response?.data?.mensagem || 
                         error.message || 
                         'Erro desconhecido ao carregar empresas';
        setMensagem(`Erro: ${errorMsg}`);
        setCarregando(false);
      });
  }, []);
  
  // Função para carregar certidões armazenadas
  const carregarCertidoes = useCallback(() => {
    setCarregandoCertidoes(true);
    const accessToken = localStorage.getItem('access');
    
    let url = `${process.env.REACT_APP_API_BASE}/api/pgfn/listar/`;
    if (filtroCertidao) {
      url += `?cnpj=${filtroCertidao}`;
    }
    
    axios
      .get(url, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      })
      .then((res) => {
        if (res.data && res.data.certidoes) {
          setCertidoes(res.data.certidoes);
          setCertidoesFiltradas(res.data.certidoes);
        } else {
          setCertidoes([]);
          setCertidoesFiltradas([]);
        }
        setCarregandoCertidoes(false);
      })
      .catch((error) => {
        console.error('Erro ao carregar certidões:', error);
        setCertidoes([]);
        setCertidoesFiltradas([]);
        setCarregandoCertidoes(false);
      });
  }, [filtroCertidao]);

  // Aplicar filtros nas certidões
  useEffect(() => {
    if (certidoes.length > 0) {
      const filtered = certidoes.filter(certidao => 
        Object.entries(filtrosCertidoes).every(([campo, valor]) => {
          if (!valor) return true; // Se o filtro estiver vazio, não filtra este campo
          
          // Tratamento especial para o campo CNPJ (formatação)
          if (campo === 'cnpj') {
            const cnpjFormatado = formatarCNPJ(certidao[campo]);
            return cnpjFormatado.toLowerCase().includes(valor.toLowerCase());
          }
          
          const valorCertidao = certidao[campo]?.toString().toLowerCase() || '';
          return valorCertidao.includes(valor.toLowerCase());
        })
      );
      setCertidoesFiltradas(filtered);
    }
  }, [certidoes, filtrosCertidoes]);

  useEffect(() => {
    carregarEmpresas();
  }, [carregarEmpresas]);
  
  useEffect(() => {
    if (activeTab === 'certidoes') {
      carregarCertidoes();
    }
  }, [activeTab, carregarCertidoes]);

  // Limpar todos os filtros de certidões
  const limparFiltrosCertidoes = () => {
    setFiltrosCertidoes({
      cnpj: '',
      razao_social: '',
      tipo_certidao: '',
      data_emissao: '',
      data_validade: '',
      codigo: ''
    });
  };

  // Função para alternar seleção de empresa
  const handleCheckbox = (cnpj) => {
    setSelecionadas((prev) =>
      prev.includes(cnpj) 
        ? prev.filter((item) => item !== cnpj) 
        : [...prev, cnpj]
    );
  };

  // Selecionar todas
  const selecionarTodas = () => {
    const empresasFiltradas = empresas.filter(emp =>
      Object.entries(filtros).every(([campo, valor]) =>
        emp[campo]?.toString().toLowerCase().includes(valor.toLowerCase())
      )
    );
    if (selecionadas.length === empresasFiltradas.length) {
      setSelecionadas([]);
    } else {
      setSelecionadas(empresasFiltradas.map(emp => emp.cnpj));
    }
  };

  // ================================
  // Gerar Certidões PGFN
  // ================================
  const gerarCertidoesPgfn = async () => {
    if (selecionadas.length === 0) {
      setMensagem('Selecione ao menos uma empresa');
      return;
    }

    const accessToken = localStorage.getItem('access');
    setGerando(true);
    setMensagem('Gerando certidões PGFN... Este processo pode demorar alguns minutos.');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/pgfn/gerar/`,
        { cnpjs: selecionadas },
        { 
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          },
          validateStatus: () => true,
          timeout: 300000 // 5 minutos de timeout
        }
      );

      const contentType = response.headers['content-type'];

      if (contentType && contentType.includes('application/json')) {
        const text = await response.data.text();
        const json = JSON.parse(text);

        if (json.empresas_com_erro) {
          const listaEmpresas = json.empresas_com_erro.map(emp => 
            `CNPJ: ${formatarCNPJ(emp.cnpj)} - ${emp.razaosocial} (${emp.erro})`
          ).join('\n');
          setMensagem(`Erro ao gerar certidões para as empresas:\n${listaEmpresas}`);
        } else {
          setMensagem(`Erro: ${json.mensagem || 'Erro desconhecido'}`);
        }
        return;
      }

      // Verificar se há empresas com erro no header
      const empresasComErro = response.headers['x-empresas-com-erro'];
      let mensagemExtra = '';
      if (empresasComErro) {
        const erros = JSON.parse(empresasComErro);
        const listaErros = erros.map(emp => 
          `${formatarCNPJ(emp.cnpj)} - ${emp.razaosocial}`
        ).join(', ');
        mensagemExtra = ` (Algumas empresas tiveram erro: ${listaErros})`;
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'certidoes_pgfn.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setMensagem(`Certidões PGFN geradas com sucesso!${mensagemExtra}`);
      
      // Recarregar certidões se estiver na tab de certidões
      if (activeTab === 'certidoes') {
        carregarCertidoes();
      }
    } catch (error) {
      console.error('Erro ao gerar certidões PGFN:', error);
      if (error.code === 'ECONNABORTED') {
        setMensagem('Timeout: O processo demorou mais que o esperado. Tente novamente com menos empresas.');
      } else {
        setMensagem(`Erro inesperado: ${error.message}`);
      }
    } finally {
      setGerando(false);
    }
  };

  // ================================
  // Gerar Segunda Via de Certidões (Lista)
  // ================================
  const abrirModalSegundaVia = () => {
    if (selecionadas.length === 0) {
      setMensagem('Selecione ao menos uma empresa');
      return;
    }
    setMostrarModalSegundaVia(true);
  };

  const gerarSegundaViaCertidoes = async () => {
    const accessToken = localStorage.getItem('access');
    setGerandoSegundaVia(true);
    setMensagem('Gerando lista de segunda via... Este processo pode demorar alguns minutos.');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/pgfn/segunda-via/`,
        { 
          cnpjs: selecionadas,
          data_inicio: datasSegundaVia.data_inicio,
          data_fim: datasSegundaVia.data_fim
        },
        { 
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          },
          validateStatus: () => true,
          timeout: 300000 // 5 minutos de timeout
        }
      );

      const contentType = response.headers['content-type'];

      if (contentType && contentType.includes('application/json')) {
        const text = await response.data.text();
        const json = JSON.parse(text);

        if (json.empresas_com_erro) {
          const listaEmpresas = json.empresas_com_erro.map(emp => 
            `CNPJ: ${formatarCNPJ(emp.cnpj)} - ${emp.razaosocial} (${emp.erro})`
          ).join('\n');
          setMensagem(`Erro ao gerar lista de segunda via para as empresas:\n${listaEmpresas}`);
        } else {
          setMensagem(`Erro: ${json.mensagem || 'Erro desconhecido'}`);
        }
        return;
      }

      // Verificar se há empresas com erro no header
      const empresasComErro = response.headers['x-empresas-com-erro'];
      let mensagemExtra = '';
      if (empresasComErro) {
        const erros = JSON.parse(empresasComErro);
        const listaErros = erros.map(emp => 
          `${formatarCNPJ(emp.cnpj)} - ${emp.razaosocial}`
        ).join(', ');
        mensagemExtra = ` (Algumas empresas tiveram erro: ${listaErros})`;
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `lista_segunda_via_pgfn_${datasSegundaVia.data_inicio}_${datasSegundaVia.data_fim}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setMensagem(`Lista de segunda via gerada com sucesso!${mensagemExtra}`);
      setMostrarModalSegundaVia(false);
      
    } catch (error) {
      console.error('Erro ao gerar lista de segunda via:', error);
      if (error.code === 'ECONNABORTED') {
        setMensagem('Timeout: O processo demorou mais que o esperado. Tente novamente com menos empresas.');
      } else {
        setMensagem(`Erro inesperado: ${error.message}`);
      }
    } finally {
      setGerandoSegundaVia(false);
    }
  };

  // ================================
  // Emitir Segunda Via Direta
  // ================================
  const emitirSegundaViaDireta = async () => {
    if (selecionadas.length === 0) {
      setMensagem('Selecione ao menos uma empresa');
      return;
    }

    const accessToken = localStorage.getItem('access');
    setEmitindoSegundaViaDireta(true);
    setMensagem('Emitindo segunda via de certidões... Este processo pode demorar alguns minutos.');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/pgfn/emitir-segunda-via/`,
        { cnpjs: selecionadas },
        { 
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          },
          validateStatus: () => true,
          timeout: 300000 // 5 minutos de timeout
        }
      );

      const contentType = response.headers['content-type'];

      if (contentType && contentType.includes('application/json')) {
        const text = await response.data.text();
        const json = JSON.parse(text);

        if (json.empresas_com_erro) {
          const listaEmpresas = json.empresas_com_erro.map(emp => 
            `CNPJ: ${formatarCNPJ(emp.cnpj)} - ${emp.razaosocial} (${emp.erro})`
          ).join('\n');
          setMensagem(`Erro ao emitir segunda via para as empresas:\n${listaEmpresas}`);
        } else {
          setMensagem(`Erro: ${json.mensagem || 'Erro desconhecido'}`);
        }
        return;
      }

      // Verificar se há empresas com erro no header
      const empresasComErro = response.headers['x-empresas-com-erro'];
      let mensagemExtra = '';
      if (empresasComErro) {
        const erros = JSON.parse(empresasComErro);
        const listaErros = erros.map(emp => 
          `${formatarCNPJ(emp.cnpj)} - ${emp.razaosocial}`
        ).join(', ');
        mensagemExtra = ` (Algumas empresas tiveram erro: ${listaErros})`;
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'segunda_via_direta_pgfn.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setMensagem(`Segunda via emitida com sucesso!${mensagemExtra}`);
      
      // Recarregar certidões se estiver na tab de certidões
      if (activeTab === 'certidoes') {
        carregarCertidoes();
      }
      
    } catch (error) {
      console.error('Erro ao emitir segunda via:', error);
      if (error.code === 'ECONNABORTED') {
        setMensagem('Timeout: O processo demorou mais que o esperado. Tente novamente com menos empresas.');
      } else {
        setMensagem(`Erro inesperado: ${error.message}`);
      }
    } finally {
      setEmitindoSegundaViaDireta(false);
    }
  };

  // Filtrar empresas
  const empresasFiltradas = empresas.filter(emp =>
    Object.entries(filtros).every(([campo, valor]) =>
      emp[campo]?.toString().toLowerCase().includes(valor.toLowerCase())
    )
  );

  // Função para classificar o status da certidão
  const getStatusClass = (diasRestantes) => {
    if (diasRestantes === null) return 'text-secondary';
    if (diasRestantes <= 0) return 'text-danger';
    if (diasRestantes <= 30) return 'text-warning';
    return 'text-success';
  };

  return (
    <div className="pgfn-page">
      <div className="container mt-5 shadow p-4 rounded bg-white">
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center">
            <i className="fas fa-file-certificate header-icon me-2"></i>
            <h2 className="titulo-pagina m-0">Certidões PGFN</h2>
          </div>
        </div>

        {/* Tabs de navegação */}
        <ul className="nav nav-tabs mb-3">
          <li className="nav-item">
            <button 
              className={`nav-link ${activeTab === 'consulta' ? 'active' : ''}`}
              onClick={() => setActiveTab('consulta')}
            >
              <i className="fas fa-search me-2"></i>
              Consultar Certidões
            </button>
          </li>
          <li className="nav-item">
            <button 
              className={`nav-link ${activeTab === 'certidoes' ? 'active' : ''}`}
              onClick={() => setActiveTab('certidoes')}
            >
              <i className="fas fa-file-alt me-2"></i>
              Certidões Armazenadas ({certidoesFiltradas.length})
            </button>
          </li>
        </ul>

        {/* Mensagens agora são exibidas como toast (react-toastify) */}

        {/* Tab de consulta */}
        {activeTab === 'consulta' && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="alert alert-info mb-0">
                <i className="fas fa-info-circle me-2"></i>
                <strong>Atenção:</strong> A geração de certidões PGFN pode demorar alguns minutos por empresa.
              </div>

              <div className="d-flex gap-2">
                <button 
                  className="btn btn-primary px-4"
                  onClick={gerarCertidoesPgfn}
                  disabled={gerando || gerandoSegundaVia || emitindoSegundaViaDireta || selecionadas.length === 0}
                >
                  {gerando ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Gerando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-file-certificate me-2"></i>
                      Gerar Certidões
                    </>
                  )}
                </button>

                <button 
                  className="btn btn-secondary px-4"
                  onClick={abrirModalSegundaVia}
                  disabled={gerando || gerandoSegundaVia || emitindoSegundaViaDireta || selecionadas.length === 0}
                >
                  <i className="fas fa-list me-2"></i>
                  Listar Segunda Via
                </button>

                <button 
                  className="btn btn-success px-4"
                  onClick={emitirSegundaViaDireta}
                  disabled={gerando || gerandoSegundaVia || emitindoSegundaViaDireta || selecionadas.length === 0}
                >
                  {emitindoSegundaViaDireta ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Emitindo...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-copy me-2"></i>
                      Emitir Segunda Via
                    </>
                  )}
                </button>
              </div>
            </div>

            {selecionadas.length > 0 && (
              <div className="alert alert-warning">
                <i className="fas fa-clock me-2"></i>
                Tempo estimado: aproximadamente {selecionadas.length * 1.5} minutos para {selecionadas.length} empresa(s).
              </div>
            )}

            <div className="table-responsive">
              {carregando ? (
                <div className="text-center mt-4">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                  <p>Carregando empresas...</p>
                </div>
              ) : (
                <table className="table table-bordered table-hover text-center align-middle fs-6">
                  <thead className="table-light">
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          aria-label="Selecionar todas as empresas"
                          checked={selecionadas.length === empresasFiltradas.length && empresasFiltradas.length > 0}
                          onChange={selecionarTodas}
                        />
                      </th>
                      <th>
                        Razão Social
                        <input
                          type="text"
                          className="form-control form-control-sm mt-1"
                          placeholder="Filtrar..."
                          value={filtros.razaosocial}
                          onChange={(e) => setFiltros(prev => ({ ...prev, razaosocial: e.target.value }))}
                        />
                      </th>
                      <th>
                        CNPJ
                        <input
                          type="text"
                          className="form-control form-control-sm mt-1"
                          placeholder="Filtrar..."
                          value={filtros.cnpj}
                          onChange={(e) => setFiltros(prev => ({ ...prev, cnpj: e.target.value }))}
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {empresasFiltradas.map((empresa) => (
                      <tr key={empresa.cnpj}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selecionadas.includes(empresa.cnpj)}
                            onChange={() => handleCheckbox(empresa.cnpj)}
                          />
                        </td>
                        <td className="text-start">{empresa.razaosocial}</td>
                        <td>{formatarCNPJ(empresa.cnpj)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              
              {!carregando && empresasFiltradas.length === 0 && (
                <div className="text-center mt-4">
                  <p className="text-muted">Nenhuma empresa encontrada.</p>
                </div>
              )}
            </div>
          </>
        )}

        {/* Tab de certidões armazenadas */}
        {activeTab === 'certidoes' && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex gap-2">
                <button 
                  className="btn btn-outline-danger btn-sm" 
                  onClick={limparFiltrosCertidoes}
                  title="Limpar todos os filtros"
                >
                  <i className="fas fa-eraser me-1"></i>
                  Limpar Filtros
                </button>
                
                <div className="input-group" style={{ maxWidth: '300px' }}>
                  <input 
                    type="text" 
                    className="form-control form-control-sm" 
                    placeholder="Busca rápida por CNPJ..." 
                    value={filtroCertidao}
                    onChange={(e) => setFiltroCertidao(e.target.value)}
                  />
                  <button 
                    className="btn btn-outline-primary btn-sm" 
                    type="button"
                    onClick={carregarCertidoes}
                  >
                    <i className="fas fa-search"></i>
                  </button>
                </div>
              </div>
              
              <button 
                className="btn btn-outline-secondary btn-sm" 
                onClick={carregarCertidoes}
                disabled={carregandoCertidoes}
              >
                <i className="fas fa-sync-alt me-2"></i>
                Atualizar
              </button>
            </div>
            
            <div className="table-responsive">
              {carregandoCertidoes ? (
                <div className="text-center mt-4">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                  <p>Carregando certidões...</p>
                </div>
              ) : (
                <table className="table table-bordered table-hover text-center align-middle fs-6">
                  <thead className="table-light">
                    <tr>
                      <th>
                        CNPJ
                        <input
                          type="text"
                          className="form-control form-control-sm mt-1"
                          placeholder="Filtrar..."
                          value={filtrosCertidoes.cnpj}
                          onChange={(e) => setFiltrosCertidoes(prev => ({ ...prev, cnpj: e.target.value }))}
                        />
                      </th>
                      <th>
                        Razão Social
                        <input
                          type="text"
                          className="form-control form-control-sm mt-1"
                          placeholder="Filtrar..."
                          value={filtrosCertidoes.razao_social}
                          onChange={(e) => setFiltrosCertidoes(prev => ({ ...prev, razao_social: e.target.value }))}
                        />
                      </th>
                      <th>
                        Tipo
                        <input
                          type="text"
                          className="form-control form-control-sm mt-1"
                          placeholder="Filtrar..."
                          value={filtrosCertidoes.tipo_certidao}
                          onChange={(e) => setFiltrosCertidoes(prev => ({ ...prev, tipo_certidao: e.target.value }))}
                        />
                      </th>
                      <th>
                        Emissão
                        <input
                          type="text"
                          className="form-control form-control-sm mt-1"
                          placeholder="DD/MM/AAAA"
                          value={filtrosCertidoes.data_emissao}
                          onChange={(e) => setFiltrosCertidoes(prev => ({ ...prev, data_emissao: e.target.value }))}
                        />
                      </th>
                      <th>
                        Validade
                        <input
                          type="text"
                          className="form-control form-control-sm mt-1"
                          placeholder="DD/MM/AAAA"
                          value={filtrosCertidoes.data_validade}
                          onChange={(e) => setFiltrosCertidoes(prev => ({ ...prev, data_validade: e.target.value }))}
                        />
                      </th>
                      <th>Status</th>
                      <th>
                        Código
                        <input
                          type="text"
                          className="form-control form-control-sm mt-1"
                          placeholder="Filtrar..."
                          value={filtrosCertidoes.codigo}
                          onChange={(e) => setFiltrosCertidoes(prev => ({ ...prev, codigo: e.target.value }))}
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {certidoesFiltradas.map((certidao) => (
                      <tr key={certidao.id}>
                        <td>{formatarCNPJ(certidao.cnpj)}</td>
                        <td className="text-start">{certidao.razao_social}</td>
                        <td>{certidao.tipo_certidao}</td>
                        <td>{certidao.data_emissao}</td>
                        <td>{certidao.data_validade}</td>
                        <td className={getStatusClass(certidao.dias_restantes)}>
                          {certidao.dias_restantes === null ? 'N/A' : 
                           certidao.dias_restantes <= 0 ? 'Vencida' : 
                           `${certidao.dias_restantes} dias`}
                        </td>
                        <td>{certidao.codigo}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              
              {!carregandoCertidoes && certidoesFiltradas.length === 0 && (
                <div className="text-center mt-4">
                  <p className="text-muted">
                    {certidoes.length === 0 ? 'Nenhuma certidão encontrada.' : 'Nenhuma certidão corresponde aos filtros aplicados.'}
                  </p>
                </div>
              )}
            </div>

            {/* Resumo dos filtros aplicados */}
            {Object.values(filtrosCertidoes).some(valor => valor !== '') && (
              <div className="mt-3">
                <small className="text-muted">
                  <i className="fas fa-filter me-1"></i>
                  Exibindo {certidoesFiltradas.length} de {certidoes.length} certidões
                </small>
              </div>
            )}
          </>
        )}

        {/* Modal para Segunda Via */}
        {mostrarModalSegundaVia && (
          <div className="modal fade show d-block" tabIndex="-1" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title mb-0 fw-bold">
                    <i className="fas fa-list me-2"></i>
                    Listar Segunda Via de Certidões
                  </h5>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setMostrarModalSegundaVia(false)}
                    disabled={gerandoSegundaVia}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="alert alert-info">
                    <i className="fas fa-info-circle me-2"></i>
                    Selecione o período para buscar certidões emitidas para as <strong>{selecionadas.length}</strong> empresa(s) selecionada(s).
                  </div>
                  
                  <div className="row">
                    <div className="col-md-6">
                      <label className="form-label">Data Início:</label>
                      <input
                        type="date"
                        className="form-control"
                        value={datasSegundaVia.data_inicio}
                        onChange={(e) => setDatasSegundaVia(prev => ({ ...prev, data_inicio: e.target.value }))}
                        disabled={gerandoSegundaVia}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Data Fim:</label>
                      <input
                        type="date"
                        className="form-control"
                        value={datasSegundaVia.data_fim}
                        onChange={(e) => setDatasSegundaVia(prev => ({ ...prev, data_fim: e.target.value }))}
                        disabled={gerandoSegundaVia}
                      />
                    </div>
                  </div>
                  
                  <div className="mt-3">
                    <small className="text-muted">
                      <i className="fas fa-calendar me-1"></i>
                      Período selecionado: {new Date(datasSegundaVia.data_inicio).toLocaleDateString('pt-BR')} até {new Date(datasSegundaVia.data_fim).toLocaleDateString('pt-BR')}
                    </small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setMostrarModalSegundaVia(false)}
                    disabled={gerandoSegundaVia}
                  >
                    Cancelar
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-primary" 
                    onClick={gerarSegundaViaCertidoes}
                    disabled={gerandoSegundaVia}
                  >
                    {gerandoSegundaVia ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Gerando...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-download me-2"></i>
                        Gerar Lista
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Pgfn;